// VideoGalleryComponent.tsx

import { h, VNode } from 'preact';
import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'preact/hooks';
import * as ReactDOM from 'preact/compat';
import { createElement, Component } from 'preact/compat';
import reactToWebComponent from 'react-to-webcomponent';

// Import CSS
import styles from './video-gallery.css';
import sliderStyles from './splide.css';

// Function to inject base styles. Slider-related styles are injected lazily
// inside loadSplide() so the load order is: official splide.min.css → custom
// splide.css. This guarantees our arrow/slide overrides win over Splide's
// defaults regardless of when each style block is parsed.
const injectStyles = () => {
  const baseStyleId = 'video-gallery-styles';
  if (!document.getElementById(baseStyleId)) {
    const baseStyle = document.createElement('style');
    baseStyle.id = baseStyleId;
    baseStyle.textContent = styles;
    document.head.appendChild(baseStyle);
  }
};

// Inject base styles as soon as the module loads (not on component mount) so
// the loading skeleton is styled — and reserves space — on its very first paint.
injectStyles();

// Expose the real vertical-scrollbar width so the full-width block breakout
// (see gallery.liquid) can subtract it — 100vw alone overhangs the viewport
// by the scrollbar width on systems with non-overlay scrollbars.
const setScrollbarWidthVar = () => {
  const width = window.innerWidth - document.documentElement.clientWidth;
  document.documentElement.style.setProperty('--videoreel-scrollbar', `${width}px`);
};
setScrollbarWidthVar();
window.addEventListener('resize', setScrollbarWidthVar);

// Import Splide only when needed
const loadSplide = async () => {
  // @ts-ignore
  const { Splide, SplideSlide } = await import('@splidejs/react-splide');

  // Inject the official splide.min.css FIRST. The build (esbuild loader:text)
  // bundles this CSS as a string — a bare `import('…/splide.min.css')` would
  // discard the result, so we have to inject it manually. Without these rules
  // Splide's slide widths don't compute correctly and mixed-content sliders
  // clip the second slide.
  // @ts-ignore
  const splideCssModule = await import('@splidejs/splide/dist/css/splide.min.css');
  const splideCssText: string =
    (splideCssModule as any).default ?? (splideCssModule as any) ?? '';
  const officialStyleId = 'video-gallery-splide-official-styles';
  if (typeof splideCssText === 'string' && splideCssText && !document.getElementById(officialStyleId)) {
    const style = document.createElement('style');
    style.id = officialStyleId;
    style.textContent = splideCssText;
    document.head.appendChild(style);
  }

  // Inject our custom slider overrides AFTER the official CSS so our arrow,
  // slide and hover styles win on equal-specificity selectors.
  const customStyleId = 'video-gallery-slider-styles';
  const existingCustom = document.getElementById(customStyleId);
  if (existingCustom) {
    // Move it to the end of <head> in case it was injected earlier.
    existingCustom.parentNode?.removeChild(existingCustom);
  }
  const customStyle = document.createElement('style');
  customStyle.id = customStyleId;
  customStyle.textContent = sliderStyles;
  document.head.appendChild(customStyle);

  return { Splide, SplideSlide };
};

interface Product {
  title: string;
  handle: string;
  imageUrl?: string | null;
  price: string;
  currency: string;
}

interface VideoLink {
  url: string;
  label: string;
  imageUrl?: string | null;
  resourceType: string;
}

interface Video {
  id: number;
  name: string;
  youtubeId: string | null;
  kind?: 'youtube' | 'upload';
  videoUrl?: string | null;
  isShort?: boolean;
  title?: string;
  description?: string | null;
  thumbnailUrl?: string;
  products?: Product[];
  links?: VideoLink[];
}

// Layout meta cached per gallery so repeat visits can reserve the exact
// gallery height synchronously (before the JSON fetch resolves) and avoid CLS.
interface CachedGalleryMeta {
  count: number;
  allShorts: boolean;
  videosPerRow: number;
  layout: 'grid' | 'slider';
  videoSize: string;
  mobileTwoPerRow?: boolean;
}

const galleryMetaKey = (galleryId: string) => `videoreel-gallery-meta-${galleryId}`;

const readCachedGalleryMeta = (galleryId: string): CachedGalleryMeta | null => {
  try {
    const raw = localStorage.getItem(galleryMetaKey(galleryId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeCachedGalleryMeta = (galleryId: string, meta: CachedGalleryMeta) => {
  try {
    localStorage.setItem(galleryMetaKey(galleryId), JSON.stringify(meta));
  } catch {
    // Storage unavailable (private mode/sandboxed iframe) — skeleton falls back to defaults.
  }
};

interface GallerySettings {
  textColor?: string;
  descriptionColor?: string | null;
  playButtonColor?: string;
  playButtonBgColor?: string;
  overlayBgColor?: string;
  closeButtonColor?: string;
  closeButtonBgColor?: string;
  productCardBgColor?: string;
  productCardTextColor?: string;
  videosPerRow?: number;
  videoSize?: string;
  layout?: 'grid' | 'slider';
  showTitles?: boolean;
  autoplay?: boolean;
  playOnHover?: boolean;
  mobileTwoPerRow?: boolean;
  shopDomain?: string;
}

// Load the YouTube IFrame API once, shared by every autoplay preview.
let ytApiPromise: Promise<any> | null = null;
const loadYouTubeApi = (): Promise<any> => {
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    const w = window as any;
    if (w.YT && w.YT.Player) {
      resolve(w.YT);
      return;
    }
    const prev = w.onYouTubeIframeAPIReady;
    w.onYouTubeIframeAPIReady = () => {
      if (typeof prev === 'function') prev();
      resolve(w.YT);
    };
    if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(script);
    }
  });
  return ytApiPromise;
};

// Muted looping preview shown in place of the thumbnail when the gallery's
// autoplay setting is on. The iframe only mounts once the tile approaches the
// viewport so a large gallery doesn't spawn a dozen players up front, and it
// stays transparent (thumbnail showing through) until the player actually
// reports PLAYING — masking YouTube's black startup frames. Looping is done
// manually via seekTo(0) on ENDED because loop=1 reloads the player and
// flashes black; around the seek the thumbnail crossfades back in. Defined at
// module scope so parent re-renders don't remount the players.
// pointer-events stay off, so clicks land on the tile and open the modal.
const AutoplayPreview = ({ video, mode = 'always' }: { video: Video; mode?: 'always' | 'hover' }) => {
  const holderRef = useRef<HTMLDivElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const playerRef = useRef<any>(null);
  const hoveredRef = useRef(false);
  const [active, setActive] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [paused, setPaused] = useState(false);

  const togglePlayback = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const player = playerRef.current;
    if (!player) return;
    if (paused) {
      player.playVideo?.();
    } else {
      player.pauseVideo?.();
    }
    setPaused(!paused);
  };

  useEffect(() => {
    if (mode !== 'always') return;
    const el = holderRef.current;
    if (!el) return;
    if (!('IntersectionObserver' in window)) {
      setActive(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setActive(true);
          io.disconnect();
        }
      },
      { rootMargin: '150px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [mode]);

  // Hover mode: the holder ignores pointer events, so listen on the tile
  // itself. First hover mounts the player (autoplay=1 starts it); later
  // hovers resume, leaving pauses and fades the thumbnail back in.
  useEffect(() => {
    if (mode !== 'hover') return;
    const tile = holderRef.current?.parentElement;
    if (!tile) return;
    const enter = () => {
      hoveredRef.current = true;
      setActive(true);
      playerRef.current?.playVideo?.();
    };
    const leave = () => {
      hoveredRef.current = false;
      playerRef.current?.pauseVideo?.();
      setPlaying(false);
    };
    tile.addEventListener('mouseenter', enter);
    tile.addEventListener('mouseleave', leave);
    return () => {
      tile.removeEventListener('mouseenter', enter);
      tile.removeEventListener('mouseleave', leave);
    };
  }, [mode]);

  // Expose the playing state on the tile so CSS can hide the play button
  // while a preview is actually running (used by hover mode).
  useEffect(() => {
    const tile = holderRef.current?.parentElement;
    if (!tile) return;
    tile.classList.toggle('vg-preview-playing', playing);
    return () => tile.classList.remove('vg-preview-playing');
  }, [playing]);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    loadYouTubeApi().then((YT) => {
      if (cancelled || !iframeRef.current || !YT?.Player) return;
      playerRef.current = new YT.Player(iframeRef.current, {
        events: {
          onReady: (event: any) => {
            event.target.mute();
            if (mode === 'always' || hoveredRef.current) {
              event.target.playVideo();
            } else {
              // The cursor left before the player was ready.
              event.target.pauseVideo();
            }
          },
          onStateChange: (event: any) => {
            if (event.data === YT.PlayerState.PLAYING) {
              setPlaying(true);
            } else if (event.data === YT.PlayerState.PAUSED) {
              if (mode === 'hover') setPlaying(false);
            } else if (event.data === YT.PlayerState.ENDED) {
              // Manual loop: fade the thumbnail back in while we rewind, so
              // the restart never shows a black frame.
              setPlaying(false);
              event.target.seekTo(0);
              if (mode === 'always' || hoveredRef.current) {
                event.target.playVideo();
              }
            }
          }
        }
      });
    });
    return () => {
      cancelled = true;
      try {
        playerRef.current?.destroy?.();
      } catch {
        // Player may already be gone; nothing to clean up.
      }
      playerRef.current = null;
    };
  }, [active]);

  if (!video.youtubeId) return null;

  return (
    <div ref={holderRef} className="video-autoplay-preview" aria-hidden="true">
      {active && (
        <iframe
          ref={iframeRef}
          className={`video-autoplay-iframe ${playing ? 'is-playing' : ''}`}
          src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?autoplay=1&mute=1&controls=0&playsinline=1&rel=0&disablekb=1&fs=0&iv_load_policy=3&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`}
          allow="autoplay; encrypted-media"
          tabIndex={-1}
          title=""
        />
      )}
      {mode === 'always' && active && (playing || paused) && (
        <button
          type="button"
          className="video-autoplay-toggle"
          onClick={togglePlayback as any}
          aria-label={paused ? 'Play preview' : 'Pause preview'}
        >
          {paused ? (
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M8 5v14l11-7z" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
            </svg>
          )}
        </button>
      )}
    </div>
  );
};

// Native <video> counterpart of AutoplayPreview for merchant-uploaded files.
// Same lazy-mount / hover / pause-toggle behavior, but none of the YouTube
// workarounds: `loop` restarts without black frames and there's no player
// chrome to crop, so the element just covers the tile.
const NativeAutoplayPreview = ({ video, mode = 'always' }: { video: Video; mode?: 'always' | 'hover' }) => {
  const holderRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hoveredRef = useRef(false);
  const [active, setActive] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [paused, setPaused] = useState(false);

  const togglePlayback = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const el = videoRef.current;
    if (!el) return;
    if (paused) {
      el.play().catch(() => {});
    } else {
      el.pause();
    }
    setPaused(!paused);
  };

  useEffect(() => {
    if (mode !== 'always') return;
    const el = holderRef.current;
    if (!el) return;
    if (!('IntersectionObserver' in window)) {
      setActive(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setActive(true);
          io.disconnect();
        }
      },
      { rootMargin: '150px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [mode]);

  // Hover mode: the holder ignores pointer events, so listen on the tile.
  useEffect(() => {
    if (mode !== 'hover') return;
    const tile = holderRef.current?.parentElement;
    if (!tile) return;
    const enter = () => {
      hoveredRef.current = true;
      setActive(true);
      videoRef.current?.play().catch(() => {});
    };
    const leave = () => {
      hoveredRef.current = false;
      videoRef.current?.pause();
      setPlaying(false);
    };
    tile.addEventListener('mouseenter', enter);
    tile.addEventListener('mouseleave', leave);
    return () => {
      tile.removeEventListener('mouseenter', enter);
      tile.removeEventListener('mouseleave', leave);
    };
  }, [mode]);

  // Expose the playing state on the tile so CSS can hide the play button
  // while a preview is actually running.
  useEffect(() => {
    const tile = holderRef.current?.parentElement;
    if (!tile) return;
    tile.classList.toggle('vg-preview-playing', playing);
    return () => tile.classList.remove('vg-preview-playing');
  }, [playing]);

  // Kick off playback once the element mounts (autoPlay attributes are
  // unreliable for dynamically inserted muted videos in some browsers).
  useEffect(() => {
    if (!active) return;
    const el = videoRef.current;
    if (!el) return;
    if (mode === 'always' || hoveredRef.current) {
      el.play().catch(() => {});
    }
  }, [active, mode]);

  if (!video.videoUrl) return null;

  return (
    <div ref={holderRef} className="video-autoplay-preview" aria-hidden="true">
      {active && (
        <video
          ref={videoRef}
          className={`video-autoplay-native ${playing ? 'is-playing' : ''}`}
          src={video.videoUrl}
          muted
          loop
          playsInline
          preload="none"
          tabIndex={-1}
          onPlaying={() => setPlaying(true)}
          onPause={() => {
            if (mode === 'hover') setPlaying(false);
          }}
        />
      )}
      {mode === 'always' && active && (playing || paused) && (
        <button
          type="button"
          className="video-autoplay-toggle"
          onClick={togglePlayback as any}
          aria-label={paused ? 'Play preview' : 'Pause preview'}
        >
          {paused ? (
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M8 5v14l11-7z" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
            </svg>
          )}
        </button>
      )}
    </div>
  );
};

const VideoGalleryComponent = ({ 'gallery-id': galleryId }: { 'gallery-id': string }) => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [activeVideo, setActiveVideo] = useState<number | null>(null);
  const portalContainer = useRef<HTMLDivElement | null>(null);
  const [cachedMeta] = useState<CachedGalleryMeta | null>(() =>
    galleryId ? readCachedGalleryMeta(galleryId) : null
  );
  // Server-rendered placeholder from the liquid block (sized via the dims
  // metafield). While it exists we render nothing so it keeps reserving the
  // exact gallery space; it's removed once the real data has arrived.
  const [ssrPlaceholder] = useState<Element | null>(() =>
    galleryId
      ? document.querySelector(
          `video-gallery[gallery-id="${galleryId}"] .vg-ssr-placeholder`
        )
      : null
  );
  // Whether the theme block enabled the full-width breakout (class set in liquid).
  const [blockFullWidth] = useState<boolean>(() =>
    galleryId
      ? !!document.querySelector(
          `video-gallery[gallery-id="${galleryId}"].videoreel-full-width`
        )
      : false
  );
  const [settings, setSettings] = useState<GallerySettings>({
    textColor: '#ffffff',
    playButtonColor: '#ffffff',
    playButtonBgColor: 'rgba(0, 0, 0, 0.7)',
    overlayBgColor: 'rgba(0, 0, 0, 0.9)',
    closeButtonColor: '#ffffff',
    closeButtonBgColor: 'rgba(0, 0, 0, 0.5)',
    videosPerRow: cachedMeta?.videosPerRow || 3,
    videoSize: cachedMeta?.videoSize || 'full',
    mobileTwoPerRow: cachedMeta?.mobileTwoPerRow === true,
    layout: cachedMeta?.layout || 'grid',
    showTitles: true
  });
  const [SplideComponent, setSplideComponent] = useState<any>(null);
  const [SplideSlideComponent, setSplideSlideComponent] = useState<any>(null);

  // "Video size: Full width" on a one-per-row gallery forces the viewport
  // breakout even when the theme block's checkbox is off. The liquid block
  // applies the same rule from the dims metafield for the pre-JS placeholder.
  const isFullWidth =
    blockFullWidth ||
    (settings.videosPerRow === 1 && (settings.videoSize || 'full') === 'full');

  // Mirror the breakout classes onto the host element so the CSS applies when
  // the breakout comes from gallery settings rather than the liquid block
  // (e.g. a gallery whose dims metafield hasn't been mirrored yet).
  useLayoutEffect(() => {
    if (!galleryId) return;
    const host = document.querySelector(`video-gallery[gallery-id="${galleryId}"]`);
    if (!host) return;
    host.classList.toggle('videoreel-full-width', isFullWidth);
    host.classList.toggle(
      'videoreel-full-bleed',
      isFullWidth && settings.videosPerRow === 1
    );
  }, [galleryId, isFullWidth, settings.videosPerRow]);

  // Contained sliders hang the arrows 1.5rem outside the track. When the
  // gallery's edge sits within that overhang of the viewport edge (e.g. a
  // theme section spanning the full page width), the arrows would land
  // outside the viewport and create horizontal overflow — pull them inside
  // the track instead (same treatment the full-width breakout gets in CSS).
  useEffect(() => {
    if (!galleryId || settings.layout !== 'slider') return;
    const host = document.querySelector(
      `video-gallery[gallery-id="${galleryId}"]`
    );
    if (!host) return;
    const update = () => {
      const overhang =
        1.5 *
        (parseFloat(getComputedStyle(document.documentElement).fontSize) || 16);
      const rect = host.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth;
      const room = Math.min(rect.left, viewportWidth - rect.right);
      host.classList.toggle('videoreel-inset-arrows', room < overhang);
    };
    update();
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
      host.classList.remove('videoreel-inset-arrows');
    };
  }, [galleryId, settings.layout, loaded]);

  // Inject styles on mount
  useEffect(() => {
    injectStyles();
  }, []);

  // Swap the server-rendered placeholder for the real gallery before the
  // browser paints, so the reserved space is handed over without a shift or
  // a double-height frame.
  useLayoutEffect(() => {
    if (loaded && ssrPlaceholder) {
      ssrPlaceholder.remove();
    }
  }, [loaded, ssrPlaceholder]);

  // Load Splide when layout is 'slider'
  useEffect(() => {
    if (settings.layout === 'slider') {
      loadSplide().then(({ Splide, SplideSlide }) => {
        setSplideComponent(() => Splide);
        setSplideSlideComponent(() => SplideSlide);
      });
    }
  }, [settings.layout]);

  // Create portal container on mount
  useEffect(() => {
    portalContainer.current = document.createElement('div');
    portalContainer.current.id = `video-modal-portal-${Math.random().toString(36).substr(2, 9)}`;
    document.body.appendChild(portalContainer.current);

    return () => {
      if (portalContainer.current) {
        document.body.removeChild(portalContainer.current);
      }
    };
  }, []);

  // Fetch gallery data
  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await fetch(`https://video-gallery-psi.vercel.app/galleries/${galleryId}`);
        const data = await response.json();
        
        if (data.error) {
          console.error('Gallery error:', data.error);
          return;
        }

        const fetchedVideos: Video[] = data.gallery.videos;
        writeCachedGalleryMeta(galleryId, {
          count: fetchedVideos.length,
          allShorts: fetchedVideos.length > 0 && fetchedVideos.every((v) => v.isShort),
          videosPerRow: data.gallery.videosPerRow || 3,
          layout: data.gallery.layout || 'grid',
          videoSize: data.gallery.videoSize || 'full',
          mobileTwoPerRow: data.gallery.mobileTwoPerRow === true,
        });

        setVideos(fetchedVideos);
        setSettings({
          textColor: data.gallery.textColor || '#ffffff',
          descriptionColor: data.gallery.descriptionColor || null,
          playButtonColor: data.gallery.playButtonColor || '#ffffff',
          playButtonBgColor: data.gallery.playButtonBgColor || 'rgba(0, 0, 0, 0.7)',
          overlayBgColor: data.gallery.overlayBgColor || 'rgba(0, 0, 0, 0.9)',
          closeButtonColor: data.gallery.closeButtonColor || '#ffffff',
          closeButtonBgColor: data.gallery.closeButtonBgColor || 'rgba(0, 0, 0, 0.5)',
          videosPerRow: data.gallery.videosPerRow || 3,
          videoSize: data.gallery.videoSize || 'full',
          layout: data.gallery.layout || 'grid',
          showTitles: data.gallery.showTitles !== false,
          autoplay: data.gallery.autoplay === true,
          playOnHover: data.gallery.playOnHover === true,
          mobileTwoPerRow: data.gallery.mobileTwoPerRow === true,
          shopDomain: data.gallery.shopDomain || undefined,
          productCardBgColor: data.gallery.productCardBgColor || 'rgba(53, 53, 52, 0.6)',
          productCardTextColor: data.gallery.productCardTextColor || '#ffffff',
        });
      } catch (error) {
        console.error('Error loading gallery:', error);
      } finally {
        setLoaded(true);
      }
    };

    if (galleryId) {
      fetchGallery();
    }
  }, [galleryId]);

  // The modal is keyed by the row id — youtubeId can be null for uploads.
  const openVideo = (videoId: number) => {
    setActiveVideo(videoId);
    document.body.style.overflow = 'hidden';
  };

  const closeVideo = () => {
    setActiveVideo(null);
    document.body.style.overflow = 'auto';
  };

  const ProductCarousel = ({ products, className }: {
    products: Product[];
    className?: string;
  }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const touchStartX = useRef(0);
    const touchEndX = useRef(0);

    const handleTouchStart = useCallback((e: TouchEvent) => {
      touchStartX.current = e.touches[0].clientX;
    }, []);

    const handleTouchEnd = useCallback((e: TouchEvent) => {
      touchEndX.current = e.changedTouches[0].clientX;
      const diff = touchStartX.current - touchEndX.current;
      if (Math.abs(diff) > 40) {
        if (diff > 0 && currentIndex < products.length - 1) {
          setCurrentIndex(currentIndex + 1);
        } else if (diff < 0 && currentIndex > 0) {
          setCurrentIndex(currentIndex - 1);
        }
      }
    }, [currentIndex, products.length]);

    const product = products[currentIndex];

    return (
      <div
        className={`video-thumbnail-product ${className || ''}`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onClick={(e) => e.stopPropagation()}
      >
        <a
          href={getProductUrl(product.handle)}
          target="_top"
          style={{ textDecoration: 'none', display: 'block' }}
        >
          <div className="video-thumbnail-product-glass" style={{ background: settings.productCardBgColor }}>
            <div className="video-thumbnail-product-row">
              {product.imageUrl && (
                <img
                  src={product.imageUrl}
                  alt={product.title}
                  className="video-thumbnail-product-image"
                />
              )}
              <div className="video-thumbnail-product-info">
                <div className="video-thumbnail-product-title" style={{ color: settings.productCardTextColor }}>{product.title}</div>
                <div className="video-thumbnail-product-price" style={{ color: settings.productCardTextColor, opacity: 0.8 }}>{formatPrice(product.price, product.currency)}</div>
              </div>
            </div>
          </div>
        </a>
        {products.length > 1 && (
          <div className="video-product-dots">
            {products.map((_, idx) => (
              <button
                key={idx}
                className={`video-product-dot ${idx === currentIndex ? 'active' : ''}`}
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setCurrentIndex(idx); }}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const LinkCtas = ({ links, className }: {
    links: VideoLink[];
    className?: string;
  }) => (
    <div
      className={`video-thumbnail-links ${className || ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      {links.map((link, idx) => {
        return (
          <a
            key={idx}
            href={link.url}
            target="_top"
            className="video-thumbnail-link-cta"
            style={{
              background: settings.productCardBgColor,
              color: settings.productCardTextColor,
            }}
          >
            <span className="video-thumbnail-link-cta-label">{link.label}</span>
            <svg
              className="video-thumbnail-link-cta-arrow"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="13 6 19 12 13 18" />
            </svg>
          </a>
        );
      })}
    </div>
  );

  // Merchant-entered caption rendered under the tile (outside the thumbnail).
  // Uses the optional descriptionColor; otherwise it inherits the theme's
  // text color on purpose, since the gallery textColor defaults to white for
  // overlaid titles and would vanish on a light page.
  const renderVideoCaption = (video: Video) =>
    video.description ? (
      <p
        className="video-item-description"
        style={settings.descriptionColor ? { color: settings.descriptionColor } : undefined}
      >
        {video.description}
      </p>
    ) : null;

  const renderVideoItem = (video: Video, isSlider = false) => (
    <div
      key={video.id}
      className={`${isSlider ? 'slider-video-item' : 'video-item'} ${video.isShort ? 'is-short' : ''}`}
      onClick={() => openVideo(video.id)}
      style={{ color: settings.textColor }} // Apply textColor to the container
    >
      <img
        className="video-thumbnail"
        src={
          video.thumbnailUrl ||
          (video.youtubeId
            ? `https://img.youtube.com/vi/${video.youtubeId}/maxresdefault.jpg`
            : 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==')
        }
        alt={video.title || video.name}
        onError={(e) => {
          const img = e.target as HTMLImageElement;
          if (video.youtubeId && img.src.includes('maxresdefault')) {
            img.src = `https://img.youtube.com/vi/${video.youtubeId}/hqdefault.jpg`;
          }
        }}
      />
      {settings.autoplay ? (
        video.videoUrl ? (
          <NativeAutoplayPreview video={video} mode="always" />
        ) : (
          <AutoplayPreview video={video} mode="always" />
        )
      ) : settings.playOnHover ? (
        video.videoUrl ? (
          <NativeAutoplayPreview video={video} mode="hover" />
        ) : (
          <AutoplayPreview video={video} mode="hover" />
        )
      ) : null}
      {/* Play Button with customized colors — hidden when the tile already
          plays an autoplay preview */}
      {!settings.autoplay && (
      <div
        className="video-play-button"
        style={{
          backgroundColor: settings.playButtonBgColor,
          borderColor: settings.playButtonColor
        }}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill={settings.playButtonColor}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M8 5v14l11-7z" />
        </svg>
      </div>
      )}
      <div className="video-bottom-overlay">
        {settings.showTitles !== false && (
          <h3 className="video-item-title" style={{ color: settings.textColor }}>
            {video.title || video.name}
          </h3>
        )}
        {video.products && video.products.length > 0 && (
          <ProductCarousel products={video.products} />
        )}
        {video.links && video.links.length > 0 && (
          <LinkCtas links={video.links} />
        )}
      </div>
    </div>
  );

  // Cap the width only when showing one video per row. Other layouts and
  // the "full" preset keep the existing full-width behavior.
  const videoSizeMaxWidth: Record<string, number> = { large: 900, medium: 700, small: 500 };
  const maxWidthPx =
    settings.videosPerRow === 1 && !isFullWidth
      ? videoSizeMaxWidth[settings.videoSize || 'full']
      : undefined;
  const containerMaxWidthStyle = maxWidthPx
    ? { maxWidth: `${maxWidthPx}px`, marginLeft: 'auto', marginRight: 'auto' }
    : {};

  // Placeholder sized like the eventual gallery so the page doesn't shift when
  // the data arrives. Dimensions come from the per-gallery localStorage cache
  // (exact on repeat visits); first-ever visits fall back to the defaults.
  const renderSkeleton = () => {
    const count = loaded ? videos.length : cachedMeta?.count ?? 3;
    const perRow = settings.videosPerRow || 3;

    if (settings.layout === 'slider') {
      return (
        <div
          className="video-skeleton-row"
          style={{ '--vg-cols': perRow } as any}
        >
          {Array.from({ length: Math.max(1, Math.min(count, perRow)) }).map((_, idx) => (
            <div key={idx} className="video-item video-skeleton-item video-skeleton-slide">
              <span className="video-skeleton-shimmer" />
            </div>
          ))}
        </div>
      );
    }

    return (
      <div
        className="video-container"
        style={{
          gridTemplateColumns: `repeat(${perRow}, 1fr)`,
          ...containerMaxWidthStyle
        }}
      >
        {Array.from({ length: Math.max(1, count) }).map((_, idx) => (
          <div key={idx} className="video-item-wrapper">
            <div className="video-item video-skeleton-item">
              <span className="video-skeleton-shimmer" />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderGallery = () => {
    if (settings.layout === 'slider' && SplideComponent && SplideSlideComponent) {
      // Full-width one-per-slide sliders fill the whole viewport: no side
      // padding (the liquid block covers the pre-JS placeholder the same way).
      const fullBleed = isFullWidth && settings.videosPerRow === 1;
      return (
        <SplideComponent
          options={{
            type: 'slide',
            perPage: settings.videosPerRow,
            perMove: 1,
            gap: '1rem',
            padding: fullBleed ? 0 : { left: '1rem', right: '1rem' },
            pagination: false,
            arrows: true,
            drag: true,
            snap: true,
            rewind: true,
            breakpoints: {
              768: {
                perPage: Math.min(2, settings.videosPerRow || 3),
                gap: '0.5rem',
                padding: fullBleed ? 0 : { left: '0.5rem', right: '0.5rem' }
              },
              480: {
                // All-shorts galleries can opt into keeping two slides per
                // view on phones; one-per-slide sliders keep their layout.
                perPage:
                  allShorts && settings.mobileTwoPerRow && settings.videosPerRow !== 1
                    ? 2
                    : 1,
                gap: '0.5rem',
                padding: fullBleed ? 0 : { left: '0.5rem', right: '0.5rem' }
              }
            }
          }}
        >
          {videos.map(video => (
            <SplideSlideComponent key={video.id}>
              <div className="video-item-wrapper">
                {renderVideoItem(video, true)}
                {renderVideoCaption(video)}
              </div>
            </SplideSlideComponent>
          ))}
        </SplideComponent>
      );
    }

    return (
      <div
        className="video-container"
        style={{
          gridTemplateColumns: `repeat(${settings.videosPerRow}, 1fr)`,
          ...containerMaxWidthStyle
        }}
      >
        {videos.map(video => (
          <div key={video.id} className="video-item-wrapper">
            {renderVideoItem(video)}
            {renderVideoCaption(video)}
          </div>
        ))}
      </div>
    );
  };

  const getProductUrl = (handle: string) => {
    if (settings.shopDomain) {
      return `https://${settings.shopDomain}/products/${handle}`;
    }
    return `/products/${handle}`;
  };

  const formatPrice = (price: string, currency: string) => {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(parseFloat(price));
    } catch {
      return `${currency} ${price}`;
    }
  };

  const activeVideoData = activeVideo ? videos.find(v => v.id === activeVideo) : null;
  const activeProducts = activeVideoData?.products || [];
  const activeLinks = activeVideoData?.links || [];
  const allShorts = loaded
    ? videos.length > 0 && videos.every(v => v.isShort)
    : cachedMeta?.allShorts ?? false;

  // The server-rendered placeholder is still reserving the space — render
  // nothing so the wrapper's padding doesn't add height next to it.
  if (!loaded && ssrPlaceholder) {
    return null;
  }

  return (
    <div className={`video-gallery video-gallery-${galleryId} ${allShorts ? 'all-shorts' : ''} ${allShorts && settings.mobileTwoPerRow ? 'mobile-two-per-row' : ''}`}>
      {!loaded || (settings.layout === 'slider' && !SplideComponent) ? (
        renderSkeleton()
      ) : (
        renderGallery()
      )}

      {activeVideo && portalContainer.current && ReactDOM.createPortal(
        <div
          className="video-modal-global"
          onClick={closeVideo}
          style={{ backgroundColor: settings.overlayBgColor }} // Apply overlayBgColor
        >
          <div
            className={`video-modal-content-global ${activeProducts.length > 0 || activeLinks.length > 0 ? 'has-products' : ''} ${activeVideoData?.isShort ? 'short' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button with customized colors */}
            <div
              className="video-modal-close-global"
              style={{
                color: settings.closeButtonColor,
                backgroundColor: settings.closeButtonBgColor
              }}
              onClick={closeVideo}
            >
              &times;
            </div>
            {videos.map(video => video.id === activeVideo && (
              <div key={video.id} className={`video-and-products ${video.isShort ? 'short' : 'normal'} ${activeProducts.length > 0 || activeLinks.length > 0 ? 'with-sidebar' : ''}`}>
                <div className={`video-wrapper ${video.isShort ? 'short' : 'normal'}`}>
                  {video.videoUrl ? (
                    <video
                      src={video.videoUrl}
                      className="video-iframe"
                      controls
                      autoPlay
                      playsInline
                      poster={video.thumbnailUrl || undefined}
                    />
                  ) : (
                    <iframe
                      src={`https://www.youtube.com/embed/${video.youtubeId}?autoplay=1`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="video-iframe"
                    />
                  )}
                </div>
                {(activeProducts.length > 0 || activeLinks.length > 0) && (
                  <div className="video-products-bar">
                    {activeProducts.length > 0 && (
                      <ProductCarousel products={activeProducts} />
                    )}
                    {activeLinks.length > 0 && (
                      <LinkCtas links={activeLinks} />
                    )}
                  </div>
                )}
                {(activeProducts.length > 0 || activeLinks.length > 0) && (
                  <div className="video-products-sidebar">
                    <div className="video-products-sidebar-header">
                      {activeProducts.length > 0 ? 'Products in video' : 'Featured links'}
                    </div>
                    <div className="video-products-sidebar-scroll">
                      {activeProducts.length > 0 && (
                        <div className="video-products-sidebar-grid">
                          {activeProducts.map((product, idx) => (
                            <a
                              key={idx}
                              href={getProductUrl(product.handle)}
                              target="_top"
                              className="video-sidebar-product-card"
                            >
                              {product.imageUrl && (
                                <img
                                  src={product.imageUrl}
                                  alt={product.title}
                                  className="video-sidebar-product-image"
                                />
                              )}
                              <div className="video-sidebar-product-info">
                                <div className="video-sidebar-product-title">{product.title}</div>
                                <div className="video-sidebar-product-price">{formatPrice(product.price, product.currency)}</div>
                              </div>
                            </a>
                          ))}
                        </div>
                      )}
                      {activeLinks.length > 0 && (
                        <div className="video-sidebar-links">
                          {activeLinks.map((link, idx) => (
                            <a key={idx} href={link.url} target="_top" className="video-sidebar-link-cta">
                              <span className="video-thumbnail-link-cta-label">{link.label}</span>
                              <svg
                                className="video-thumbnail-link-cta-arrow"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                              >
                                <line x1="5" y1="12" x2="19" y2="12" />
                                <polyline points="13 6 19 12 13 18" />
                              </svg>
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>,
        portalContainer.current
      ) as any}
    </div>
  );
};

// Convert Preact component to Web Component
const VideoGalleryWebComponent = reactToWebComponent(
  VideoGalleryComponent,
  {
    createElement: createElement as any,
    Component: Component as any,
  },
  ReactDOM as any,
  {
    props: ['gallery-id'],
    shadow: false,
  }
);

// Register the Web Component
if (!customElements.get('video-gallery')) {
  customElements.define('video-gallery', VideoGalleryWebComponent);
}

export default VideoGalleryWebComponent;
